import * as Api from '@ViewModels';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '@WebComponents/MultipleSelect';
import React from 'react';
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../../web/components/Select';
import { EditableMultipleSelect, EditableSelect, EditableToggle } from '../context';
import { useSearchableFieldFilter } from '../hooks/useSearchableFieldFilter';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

interface ITagSettingsGroupProps {
	tagSettings: Api.ITagSettings;
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setTagsSettings: (settings: Api.ITagSettings) => void;
}

enum TagSettingType {
	State = 'State',
	City = 'City',
	PostalCode = 'PostalCode',
	Country = 'Country',
}

enum MyCaseTagSettingType {
	GroupPracticeArea = 'GroupPracticeArea',
	PeopleGroup = 'PeopleGroup',
	CaseNumber = 'CaseNumber',
	CaseName = 'CaseName',
}

enum ServiceTitanTagSettingType {
	DoNotMail = 'DoNotMail',
	DoNotService = 'DoNotService',
	CustomerType = 'CustomerType',
	BusinessUnit = 'BusinessUnit',
	MembershipStatus = 'MembershipStatus',
	MembershipFollowUpStatus = 'MembershipFollowUpStatus',
	MembershipTypeName = 'MembershipTypeName',
	ServiceAgreementName = 'ServiceAgreementName',
	ServiceAgreementStatus = 'ServiceAgreementStatus',
}

enum QuickBooksOnlineTagSettingType {
	Overdue = 'Overdue',
	LastInvoice = 'LastInvoice',
	ContactType = 'ContactType',
	Active = 'Active',
}

const attorneyTagOptions = [
	{ label: 'None', value: Api.AttorneyTagOption.None },
	{ label: 'Most Recent', value: Api.AttorneyTagOption.MostRecent },
	{ label: 'All', value: Api.AttorneyTagOption.All },
];

type TagSettings =
	| Partial<Api.ITagSettings>
	| Partial<Api.IPracticePantherTagSettings>
	| Partial<Api.IMicrosoftDynamics365SalesTagSettings>
	| Partial<Api.IMyCaseTagSettings>
	| Partial<Api.IServiceTitanTagSettings>
	| Partial<Api.ISalesforceTagSettings>
	| Partial<Api.IQuickBooksOnlineTagSettings>;

export const TagSettingsGroup: React.FC<ITagSettingsGroupProps> = ({
	tagSettings,
	configuration,
	provider,
	setTagsSettings,
}) => {
	const {
		searchValue: customFieldSearchInputValue,
		handleSearchChange: handleCustomFieldSearchChange,
		handleSearchClear: handleCustomFieldSearchClear,
		filterItems,
	} = useSearchableFieldFilter();

	const isPracticePantherSettings = provider === Api.IntegrationProvider.PracticePanther;
	const ppTagSettings = isPracticePantherSettings ? (tagSettings as Api.IPracticePantherTagSettings) : null;

	const isMicrosoftDynamics365SalesSettings = provider === Api.IntegrationProvider.MicrosoftDynamics365Sales;
	const microsoftDynamics365SalesTagSettings = isMicrosoftDynamics365SalesSettings
		? (tagSettings as Api.IMicrosoftDynamics365SalesTagSettings)
		: null;

	const isMyCaseSettings = provider === Api.IntegrationProvider.MyCase;
	const myCaseTagSettings = isMyCaseSettings ? (tagSettings as Api.IMyCaseTagSettings) : null;

	const isServiceTitanSettings = provider === Api.IntegrationProvider.ServiceTitan;
	const serviceTitanTagSettings = isServiceTitanSettings ? (tagSettings as Api.IServiceTitanTagSettings) : null;

	const isSalesforceSettings = provider === Api.IntegrationProvider.Salesforce;
	const salesforceTagSettings = isSalesforceSettings ? (tagSettings as Api.ISalesforceTagSettings) : null;
	const salesforceConfiguration = isSalesforceSettings
		? (configuration as Api.ISalesforceIntegrationConfiguration)
		: null;
	const allAvailableSalesforceFields = {
		...salesforceConfiguration?.availableFields,
		...salesforceConfiguration?.availableCustomFields,
		...salesforceConfiguration?.availableDateFields,
		...salesforceConfiguration?.availableCustomDateFields,
	};
	const unsubscribeIndicatorOptions: { label: string; value: string }[] = [];
	Object.entries(allAvailableSalesforceFields ?? {}).forEach(([id, text]) => {
		unsubscribeIndicatorOptions.push({
			label: text,
			value: id,
		});
	});

	const isQBOSettings = provider === Api.IntegrationProvider.QuickBooksOnline;
	const qboTagSettings = isQBOSettings ? (tagSettings as Api.IQuickBooksOnlineTagSettings) : null;

	// Helper to get enabled tags as array
	const getEnabledTags = (): string[] => {
		const enabledTags: string[] = [];
		if (!tagSettings) return enabledTags;
		if (tagSettings.stateEnabled) enabledTags.push(TagSettingType.State);
		if (tagSettings.cityEnabled) enabledTags.push(TagSettingType.City);
		if (tagSettings.postalCodeEnabled) enabledTags.push(TagSettingType.PostalCode);
		if (tagSettings.countryEnabled) enabledTags.push(TagSettingType.Country);
		if (isMyCaseSettings) {
			if (myCaseTagSettings?.groupPracticeAreaTagEnabled) enabledTags.push(MyCaseTagSettingType.GroupPracticeArea);
			if (myCaseTagSettings?.peopleGroupTagEnabled) enabledTags.push(MyCaseTagSettingType.PeopleGroup);
			if (myCaseTagSettings?.caseNumberTagEnabled) enabledTags.push(MyCaseTagSettingType.CaseNumber);
			if (myCaseTagSettings?.caseNameTagEnabled) enabledTags.push(MyCaseTagSettingType.CaseName);
		}

		if (isServiceTitanSettings) {
			if (serviceTitanTagSettings?.doNotMailTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.DoNotMail);
			}

			if (serviceTitanTagSettings?.doNotServiceTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.DoNotService);
			}

			if (serviceTitanTagSettings?.customerTypeTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.CustomerType);
			}

			if (serviceTitanTagSettings?.businessUnitTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.BusinessUnit);
			}

			if (serviceTitanTagSettings?.membershipStatusTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.MembershipStatus);
			}

			if (serviceTitanTagSettings?.membershipFollowUpStatusTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.MembershipFollowUpStatus);
			}

			if (serviceTitanTagSettings?.membershipTypeNameTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.MembershipTypeName);
			}

			if (serviceTitanTagSettings?.serviceAgreementNameTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.ServiceAgreementName);
			}

			if (serviceTitanTagSettings?.serviceAgreementStatusTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.ServiceAgreementStatus);
			}
		}

		if (isSalesforceSettings && salesforceTagSettings) {
			(salesforceTagSettings.enabledFields || []).forEach(fieldId => {
				enabledTags.push(fieldId);
			});
		}

		if (isQBOSettings && qboTagSettings) {
			if (qboTagSettings?.overdueTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.Overdue);
			if (qboTagSettings?.lastInvoiceTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.LastInvoice);
			if (qboTagSettings?.contactTypeTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.ContactType);
			if (qboTagSettings?.activeTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.Active);
		}

		return enabledTags;
	};

	const onUpdate = async (updatedSettings: TagSettings) => {
		setTagsSettings({
			...tagSettings,
			...updatedSettings,
		});
	};

	// Handle tag selection changes
	const handleTagChange = (selectedTags: string[]) => {
		let updatedSettings: TagSettings = {
			stateEnabled: selectedTags.includes(TagSettingType.State),
			cityEnabled: selectedTags.includes(TagSettingType.City),
			postalCodeEnabled: selectedTags.includes(TagSettingType.PostalCode),
			countryEnabled: selectedTags.includes(TagSettingType.Country),
		};

		// Add MyCase specific tags if applicable
		if (isMyCaseSettings) {
			updatedSettings = {
				...updatedSettings,
				groupPracticeAreaTagEnabled: selectedTags.includes(MyCaseTagSettingType.GroupPracticeArea),
				peopleGroupTagEnabled: selectedTags.includes(MyCaseTagSettingType.PeopleGroup),
				caseNumberTagEnabled: selectedTags.includes(MyCaseTagSettingType.CaseNumber),
				caseNameTagEnabled: selectedTags.includes(MyCaseTagSettingType.CaseName),
			};
		}

		// Add ServiceTitan specific tags
		if (isServiceTitanSettings) {
			updatedSettings = {
				...updatedSettings,
				doNotMailTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.DoNotMail),
				doNotServiceTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.DoNotService),
				customerTypeTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.CustomerType),
				businessUnitTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.BusinessUnit),
				membershipStatusTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.MembershipStatus),
				membershipFollowUpStatusTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.MembershipFollowUpStatus),
				membershipTypeNameTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.MembershipTypeName),
				serviceAgreementNameTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.ServiceAgreementName),
				serviceAgreementStatusTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.ServiceAgreementStatus),
			};
		}

		// Add Salesforce fields
		if (isSalesforceSettings) {
			const fieldNames = Object.keys(salesforceConfiguration.availableFields || {});
			const enabledFields = selectedTags.filter(tag => fieldNames.includes(tag));

			updatedSettings = {
				...updatedSettings,
				enabledFields,
			};
		}

		// Add QuickBooksOnline specific tags
		if (isQBOSettings) {
			updatedSettings = {
				...updatedSettings,
				overdueTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.Overdue),
				lastInvoiceTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.LastInvoice),
				contactTypeTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.ContactType),
				activeTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.Active),
			};
		}

		onUpdate(updatedSettings);
	};

	const handleCustomFieldChange = (selectedCustomFields: string[]) => {
		onUpdate({ enabledCustomFields: selectedCustomFields });
	};

	const handleAccountTagsChange = (checked: boolean) => {
		onUpdate({ accountTagsEnabled: checked });
	};

	const handleAssignedUsersTagsChange = (checked: boolean) => {
		onUpdate({ assignedUsersTagsEnabled: checked });
	};

	const handleLeadAttorneyTagChange = (value: Api.AttorneyTagOption) => {
		onUpdate({ leadAttorneyTag: value });
	};

	const handleOriginatingAttorneyTagChange = (value: Api.AttorneyTagOption) => {
		onUpdate({ originatingAttorneyTag: value });
	};

	const getRemoteFieldDisplayName = (fieldId: string, availableFields: Record<string, string>) => {
		return availableFields?.[fieldId] || fieldId;
	};

	const getRemoteFieldsDisplayText = (
		fieldIds: string[] = [],
		availableFields: Record<string, string> = configuration.availableCustomFields
	) => {
		return fieldIds.map(fieldId => getRemoteFieldDisplayName(fieldId, availableFields));
	};

	const getRemoteFieldsLabel = (
		fieldIds: string[] = [],
		availableFields: Record<string, string> = configuration.availableCustomFields
	) => {
		const displayText = getRemoteFieldsDisplayText(fieldIds, availableFields);
		if (!displayText?.length) return 'None selected';
		return displayText.join(', ');
	};

	const getEnabledTagsDisplayNames = (): string[] => {
		const enabledTags = getEnabledTags();

		// Convert Salesforce field IDs to display names
		if (salesforceConfiguration) {
			return enabledTags.map(tag => getRemoteFieldDisplayName(tag, salesforceConfiguration.availableFields));
		}

		return enabledTags;
	};

	const getTagsDisplay = (tags: string[] = []) => {
		if (!tags?.length) return 'None selected';

		// Convert Salesforce field IDs to display names
		if (salesforceConfiguration) {
			return getRemoteFieldsLabel(tags, salesforceConfiguration.availableFields);
		}

		return tags.join(', ');
	};

	const getAttorneyTagOptionDisplay = (option: Api.AttorneyTagOption | null) => {
		if (!option) return 'None';
		switch (option) {
			case Api.AttorneyTagOption.None:
				return 'None';
			case Api.AttorneyTagOption.MostRecent:
				return 'Most Recent';
			case Api.AttorneyTagOption.All:
				return 'All';
			default:
				return 'Unknown';
		}
	};

	return (
		<SettingGroup title='Tag Settings'>
			<SettingRow title='Tags' subtitle='Select taggable information to associate with contacts'>
				<EditableMultipleSelect
					value={getEnabledTags()}
					onChange={handleTagChange}
					displayValue={getTagsDisplay(getEnabledTags())}
				>
					<MultipleSelectTrigger styles={styleSheet.editableField}>
						<MultipleSelectValue values={getEnabledTagsDisplayNames()} placeholder='Select tag types' />
					</MultipleSelectTrigger>
					<MultipleSelectContent>
						{/* We do not need to show Location fields for Salesforce since we allow base object fields to be used as tags */}
						{provider !== Api.IntegrationProvider.Salesforce && (
							<>
								<MultipleSelectItem value={TagSettingType.State} selected={tagSettings?.stateEnabled}>
									State
								</MultipleSelectItem>
								<MultipleSelectItem value={TagSettingType.City} selected={tagSettings?.cityEnabled}>
									City
								</MultipleSelectItem>
								<MultipleSelectItem value={TagSettingType.PostalCode} selected={tagSettings?.postalCodeEnabled}>
									Postal Code
								</MultipleSelectItem>
								<MultipleSelectItem value={TagSettingType.Country} selected={tagSettings?.countryEnabled}>
									Country
								</MultipleSelectItem>
							</>
						)}
						{isMyCaseSettings && myCaseTagSettings && (
							<>
								<MultipleSelectItem
									value={MyCaseTagSettingType.GroupPracticeArea}
									selected={myCaseTagSettings.groupPracticeAreaTagEnabled}
								>
									Group Practice Area
								</MultipleSelectItem>
								<MultipleSelectItem
									value={MyCaseTagSettingType.PeopleGroup}
									selected={myCaseTagSettings.peopleGroupTagEnabled}
								>
									People Group
								</MultipleSelectItem>
								<MultipleSelectItem
									value={MyCaseTagSettingType.CaseNumber}
									selected={myCaseTagSettings.caseNumberTagEnabled}
								>
									Case Number
								</MultipleSelectItem>
								<MultipleSelectItem
									value={MyCaseTagSettingType.CaseName}
									selected={myCaseTagSettings.caseNameTagEnabled}
								>
									Case Name
								</MultipleSelectItem>
							</>
						)}
						{isServiceTitanSettings && serviceTitanTagSettings && (
							<>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.DoNotMail}
									selected={serviceTitanTagSettings.doNotMailTagEnabled}
								>
									Do Not Mail
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.DoNotService}
									selected={serviceTitanTagSettings.doNotServiceTagEnabled}
								>
									Do Not Service
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.CustomerType}
									selected={serviceTitanTagSettings.customerTypeTagEnabled}
								>
									Customer Type
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.BusinessUnit}
									selected={serviceTitanTagSettings.businessUnitTagEnabled}
								>
									Business Unit
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.MembershipStatus}
									selected={serviceTitanTagSettings.membershipStatusTagEnabled}
								>
									Membership Status
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.MembershipFollowUpStatus}
									selected={serviceTitanTagSettings.membershipFollowUpStatusTagEnabled}
								>
									Membership Follow-Up Status
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.MembershipTypeName}
									selected={serviceTitanTagSettings.membershipTypeNameTagEnabled}
								>
									Membership Type Name
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.ServiceAgreementName}
									selected={serviceTitanTagSettings.serviceAgreementNameTagEnabled}
								>
									Service Agreement Name
								</MultipleSelectItem>
								<MultipleSelectItem
									value={ServiceTitanTagSettingType.ServiceAgreementStatus}
									selected={serviceTitanTagSettings.serviceAgreementStatusTagEnabled}
								>
									Service Agreement Status
								</MultipleSelectItem>
							</>
						)}
						{isSalesforceSettings && salesforceTagSettings && (
							<>
								{Object.entries(salesforceConfiguration.availableFields || {}).map(([fieldName, displayName]) => {
									const isSelected = salesforceTagSettings.enabledFields?.includes(fieldName);
									return (
										<MultipleSelectItem key={fieldName} value={fieldName} selected={isSelected}>
											{displayName}
										</MultipleSelectItem>
									);
								})}
							</>
						)}
						{isQBOSettings && qboTagSettings && (
							<>
								<MultipleSelectItem
									value={QuickBooksOnlineTagSettingType.Overdue}
									selected={qboTagSettings.overdueTagEnabled}
								>
									Overdue
								</MultipleSelectItem>
								<MultipleSelectItem
									value={QuickBooksOnlineTagSettingType.LastInvoice}
									selected={qboTagSettings.lastInvoiceTagEnabled}
								>
									Last Invoice
								</MultipleSelectItem>
								<MultipleSelectItem
									value={QuickBooksOnlineTagSettingType.ContactType}
									selected={qboTagSettings.contactTypeTagEnabled}
								>
									Contact Type
								</MultipleSelectItem>
								<MultipleSelectItem
									value={QuickBooksOnlineTagSettingType.Active}
									selected={qboTagSettings.activeTagEnabled}
								>
									Active
								</MultipleSelectItem>
							</>
						)}
					</MultipleSelectContent>
				</EditableMultipleSelect>
			</SettingRow>

			{/* Provider-specific settings */}
			{isPracticePantherSettings && ppTagSettings && (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
						<EditableMultipleSelect
							value={ppTagSettings.enabledCustomFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getRemoteFieldsLabel(ppTagSettings.enabledCustomFields)}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={getRemoteFieldsDisplayText(ppTagSettings.enabledCustomFields) || []}
									placeholder='Select custom fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent
								onSearchValueChange={handleCustomFieldSearchChange}
								isSearchable={true}
								searchFieldPlaceholder='Search custom fields'
								searchInput={customFieldSearchInputValue}
								onSearchClear={handleCustomFieldSearchClear}
							>
								{filterItems(Object.entries(configuration.availableCustomFields || {}), ([_, text]) => text).map(
									([id, text]) => {
										const isSelected = ppTagSettings.enabledCustomFields?.includes(id);
										return (
											<MultipleSelectItem key={id} value={id} selected={isSelected}>
												{text}
											</MultipleSelectItem>
										);
									}
								)}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>

					<SettingRow
						title='Account Tags'
						subtitle='Enable automatic tagging of contacts based on their associated accounts'
					>
						<EditableToggle
							id='accountTags'
							isOn={ppTagSettings.accountTagsEnabled}
							onToggleCheckChanged={handleAccountTagsChange}
						/>
					</SettingRow>

					<SettingRow
						title='Assigned Users Tags'
						subtitle='Enable automatic tagging of contacts based on assigned users'
					>
						<EditableToggle
							id='assignedUsersTags'
							isOn={ppTagSettings.assignedUsersTagsEnabled}
							onToggleCheckChanged={handleAssignedUsersTagsChange}
						/>
					</SettingRow>
				</>
			)}
			{isMicrosoftDynamics365SalesSettings && microsoftDynamics365SalesTagSettings && (
				<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
					<EditableMultipleSelect
						value={microsoftDynamics365SalesTagSettings.enabledCustomFields || []}
						onChange={handleCustomFieldChange}
						displayValue={getRemoteFieldsLabel(microsoftDynamics365SalesTagSettings.enabledCustomFields)}
					>
						<MultipleSelectTrigger styles={styleSheet.editableField}>
							<MultipleSelectValue
								values={getRemoteFieldsDisplayText(microsoftDynamics365SalesTagSettings.enabledCustomFields) || []}
								placeholder='Select custom fields'
							/>
						</MultipleSelectTrigger>
						<MultipleSelectContent
							onSearchValueChange={handleCustomFieldSearchChange}
							isSearchable={true}
							searchFieldPlaceholder='Search custom fields'
							searchInput={customFieldSearchInputValue}
							onSearchClear={handleCustomFieldSearchClear}
						>
							{filterItems(Object.entries(configuration.availableCustomFields || {}), ([_, text]) => text).map(
								([id, text]) => {
									const isSelected = microsoftDynamics365SalesTagSettings.enabledCustomFields?.includes(id);
									return (
										<MultipleSelectItem key={id} value={id} selected={isSelected}>
											{text}
										</MultipleSelectItem>
									);
								}
							)}
						</MultipleSelectContent>
					</EditableMultipleSelect>
				</SettingRow>
			)}
			{isMyCaseSettings && myCaseTagSettings && (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
						<EditableMultipleSelect
							value={myCaseTagSettings.enabledCustomFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getRemoteFieldsLabel(myCaseTagSettings.enabledCustomFields)}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={getRemoteFieldsDisplayText(myCaseTagSettings.enabledCustomFields) || []}
									placeholder='Select custom fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent
								onSearchValueChange={handleCustomFieldSearchChange}
								isSearchable={true}
								searchFieldPlaceholder='Search custom fields'
								searchInput={customFieldSearchInputValue}
								onSearchClear={handleCustomFieldSearchClear}
							>
								{filterItems(Object.entries(configuration.availableCustomFields || {}), ([_, text]) => text).map(
									([id, text]) => {
										const isSelected = myCaseTagSettings.enabledCustomFields?.includes(id);
										return (
											<MultipleSelectItem key={id} value={id} selected={isSelected}>
												{text}
											</MultipleSelectItem>
										);
									}
								)}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
					<SettingRow
						title='Lead Attorney Tag Options'
						subtitle='Control how lead attorney information is tagged on contacts'
					>
						<EditableSelect
							items={attorneyTagOptions}
							value={myCaseTagSettings?.leadAttorneyTag ?? null}
							onValueChange={handleLeadAttorneyTagChange}
							displayValue={getAttorneyTagOptionDisplay(myCaseTagSettings?.leadAttorneyTag) || null}
						>
							<SelectTrigger styles={styleSheet.editableField}>
								<SelectValue placeholder='Select lead attorney tag options' />
							</SelectTrigger>
							<SelectContent>
								{attorneyTagOptions.map(o => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</EditableSelect>
					</SettingRow>
					<SettingRow
						title='Originating Attorney Tag Options'
						subtitle='Control how originating attorney information is tagged on contacts'
					>
						<EditableSelect
							items={attorneyTagOptions}
							value={myCaseTagSettings?.originatingAttorneyTag ?? null}
							onValueChange={handleOriginatingAttorneyTagChange}
							displayValue={getAttorneyTagOptionDisplay(myCaseTagSettings?.originatingAttorneyTag) || null}
						>
							<SelectTrigger styles={styleSheet.editableField}>
								<SelectValue placeholder='Select originating attorney tag options' />
							</SelectTrigger>
							<SelectContent>
								{attorneyTagOptions.map(o => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</EditableSelect>
					</SettingRow>
				</>
			)}
			{isServiceTitanSettings && serviceTitanTagSettings && (
				<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
					<EditableMultipleSelect
						value={serviceTitanTagSettings.enabledCustomFields || []}
						onChange={handleCustomFieldChange}
						displayValue={getRemoteFieldsLabel(serviceTitanTagSettings.enabledCustomFields)}
					>
						<MultipleSelectTrigger styles={styleSheet.editableField}>
							<MultipleSelectValue
								values={getRemoteFieldsDisplayText(serviceTitanTagSettings.enabledCustomFields) || []}
								placeholder='Select custom fields'
							/>
						</MultipleSelectTrigger>
						<MultipleSelectContent
							onSearchValueChange={handleCustomFieldSearchChange}
							isSearchable={true}
							searchFieldPlaceholder='Search custom fields'
							searchInput={customFieldSearchInputValue}
							onSearchClear={handleCustomFieldSearchClear}
						>
							{filterItems(Object.entries(configuration.availableCustomFields || {}), ([_, text]) => text).map(
								([id, text]) => {
									const isSelected = serviceTitanTagSettings.enabledCustomFields?.includes(id);
									return (
										<MultipleSelectItem key={id} value={id} selected={isSelected}>
											{text}
										</MultipleSelectItem>
									);
								}
							)}
						</MultipleSelectContent>
					</EditableMultipleSelect>
				</SettingRow>
			)}
			{isSalesforceSettings && salesforceTagSettings ? (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
						<EditableMultipleSelect
							value={salesforceTagSettings.enabledCustomFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getRemoteFieldsLabel(salesforceTagSettings.enabledCustomFields)}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={getRemoteFieldsDisplayText(salesforceTagSettings.enabledCustomFields) || []}
									placeholder='Select custom fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								{Object.entries(configuration.availableCustomFields || {}).map(([id, text]) => {
									const isSelected = salesforceTagSettings.enabledCustomFields?.includes(id);
									return (
										<MultipleSelectItem key={id} value={id} selected={isSelected}>
											{text}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
					<SettingRow title='Unsubscribe Indicators' subtitle='Maps Salesforce fields to mark contacts as unsubscribed'>
						<EditableMultipleSelect
							value={salesforceTagSettings?.unsubscribeFields || []}
							onChange={(selectedFields: string[]) => {
								onUpdate({ unsubscribeFields: selectedFields });
							}}
							displayValue={getRemoteFieldsLabel(
								salesforceTagSettings?.unsubscribeFields,
								allAvailableSalesforceFields
							)}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue
									values={getRemoteFieldsDisplayText(
										salesforceTagSettings?.unsubscribeFields,
										allAvailableSalesforceFields
									)}
									placeholder='Select unsubscribe indicator fields'
								/>
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								{unsubscribeIndicatorOptions.map(o => {
									const isSelected = salesforceTagSettings?.unsubscribeFields?.includes(o.value);
									return (
										<MultipleSelectItem key={o.value} value={o.value} selected={isSelected}>
											{o.label}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
				</>
			) : null}
		</SettingGroup>
	);
};
